'use client'

import Markdown from 'markdown-to-jsx'
import { useRouter, useSearchParams } from 'next/navigation'
import React, { useMemo } from 'react'

import Flag from '@/legacy-src/components/Flag'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/legacy-src/components/ui/tooltip'
import WarningBox from '@/legacy-src/components/WarningBox'
import { LOCALE, type Locale } from '@/legacy-src/constants/locale.constants'
import { getTldFromEnvCode } from '@/legacy-src/lib/octopus/utils'
import { type SiblingCompany } from '@/legacy-src/types/UserStateType'
import { getApiHearmeLink } from '@/legacy-src/utilities/apiUtils'
import { getLocalisedLanguageName, getTranslate } from '@/legacy-src/utilities/i18n'

import { Button } from '@/src/domains/design-system/components/Button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/src/domains/design-system/components/Dialog'

interface CompanyPublicLanguageSelectorProps {
  siblingCompanies?: SiblingCompany[]
}

const FlagButton: React.FC<{ language: string; isSelected: boolean; onClick: () => void }> = ({
  language,
  isSelected,
  onClick,
}) => {
  return (
    <Button size="small" variant={isSelected ? 'petrol' : 'secondary'} onClick={onClick}>
      <Flag languageCode={language} />
      {getLocalisedLanguageName(language)}
    </Button>
  )
}

const mockSiblingCompanies: SiblingCompany[] = [
  {
    id: 1,
    name: 'Hedepy US',
    slug: 'hedep-us',
    envCode: 'en',
    therapistLanguages: ['en', 'el'],
  },
  {
    id: 1,
    name: 'Hedepy CZ',
    slug: 'hedep-cz',
    envCode: 'test',
    therapistLanguages: ['cs', 'sk'],
  },
]

export const CompanyPublicLanguageSelector = ({
  siblingCompanies: siblingCompaniesFromProps,
}: CompanyPublicLanguageSelectorProps) => {
  const router = useRouter()
  const { t } = getTranslate()
  const searchParams = useSearchParams()

  const sharedCode = searchParams?.get('sharedCode')
  const policyCode = searchParams?.get('policyCode')

  // we are mocking these on test because test-master instance only has one child
  const siblingCompanies =
    process.env.NEXT_PUBLIC_COUNTRY_IDENTIFIER === 'test' ? mockSiblingCompanies : siblingCompaniesFromProps

  const [selectedRemoteLanguage, setSelectedRemoteLanguage] = React.useState<[string, string] | null>(null)

  const languageToUrlMap = useMemo(() => {
    const map = new Map<string, string | null>()
    siblingCompanies?.forEach((company) => {
      company.therapistLanguages.forEach((lang) => {
        let url
        const tld = getTldFromEnvCode(company.envCode)

        if (company.envCode === process.env.NEXT_PUBLIC_OCTOPUS_ENV_CODE) {
          url = null
        } else if (sharedCode && policyCode) {
          url = getApiHearmeLink(tld, sharedCode, policyCode)
        } else if (company.envCode === 'test') {
          url = 'https://test-app.hedepy.cz'
        } else {
          url = `https://hedepy.${tld}/company/${company.slug}`
        }
        map.set(lang, url)
      })
    })
    return map
  }, [siblingCompanies, sharedCode, policyCode])

  const [selectedLanguage, setSelectedLanguage] = React.useState(LOCALE)

  if (!siblingCompanies || siblingCompanies.length < 2) {
    return null
  }

  const onClickLocalLanguage = (language: string) => setSelectedLanguage(language as Locale)
  const onClickOtherLanguage = ([language, url]: [string, string]) => {
    setSelectedRemoteLanguage([language, url])
  }

  return (
    <div className="mb-4">
      <div className="mb-3 text-lg font-bold">
        {t('component.company.languageSelector.title')}
        <Tooltip>
          <TooltipTrigger className="relative -top-0.5 inline-flex" variant="dark">
            ?
          </TooltipTrigger>
          <TooltipContent>{t('component.company.languageSelector.tooltip')}</TooltipContent>
        </Tooltip>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {Array.from(languageToUrlMap.entries())
          // sort LOCALE first, otherwise alphabetically
          .sort((a, b) => (a[0] === LOCALE ? -1 : b[0] === LOCALE ? 1 : b[0].localeCompare(a[0])))
          .map(([language, url]) => (
            <FlagButton
              key={language}
              language={language}
              isSelected={language === selectedLanguage}
              onClick={
                url === null ? () => onClickLocalLanguage(language) : () => onClickOtherLanguage([language, url])
              }
            />
          ))}
        <Dialog open={!!selectedRemoteLanguage} onOpenChange={() => setSelectedRemoteLanguage(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {t('component.company.languageSelector.modal.title', {
                  // url without protocol and part after first slash
                  domain: selectedRemoteLanguage?.[1]?.replace(/^https?:\/\/(api\.)?/, '').split('/')[0] || '',
                })}
              </DialogTitle>
              <DialogDescription>
                <Markdown>
                  {t('component.company.languageSelector.modal.text', {
                    language: selectedRemoteLanguage?.[0] ? getLocalisedLanguageName(selectedRemoteLanguage?.[0]) : '',
                  })}
                </Markdown>
              </DialogDescription>
              <WarningBox className="!mt-6" text={t('component.company.languageSelector.modal.note')} />
            </DialogHeader>
            <div className="mt-4 grid grid-cols-1 gap-6 tablet:grid-cols-2">
              <Button
                size="medium"
                // variant="yellow"
                onClick={selectedRemoteLanguage ? () => router.push(selectedRemoteLanguage[1]) : undefined}
              >
                {t('component.button.changePlatform')}
              </Button>
              <Button
                size="medium"
                variant="secondary"
                onClick={selectedRemoteLanguage ? () => setSelectedRemoteLanguage(null) : undefined}
              >
                {t('component.button.iWillStay')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  )
}
